import { Link, useParams } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { useLang } from "../App";

const CONTENT = {
  regulamin: {
    title: { pl: "Regulamin", en: "Terms & Conditions" },
    body: {
      pl: [
        ["1. Postanowienia ogólne", "Niniejszy regulamin określa zasady korzystania ze sklepu internetowego BOHATERO oraz zawierania umów sprzedaży personalizowanych, drukowanych książek dla dzieci."],
        ["2. Zamówienia", "Zamówienie składane jest poprzez wybór pakietu, wypełnienie formularza personalizacji oraz dokonanie płatności. Po złożeniu zamówienia skontaktujemy się w celu potwierdzenia szczegółów i bezpiecznego odbioru zdjęć."],
        ["3. Produkt personalizowany", "Każda książka jest tworzona indywidualnie na podstawie przesłanych informacji i zdjęć. Ilustracje inspirowane są wyglądem dziecka i zachowują jego charakterystyczne cechy."],
        ["4. Płatności", "Płatności realizowane są za pośrednictwem bezpiecznego operatora płatności. Ceny podane są w złotych i zawierają wartość produktu."],
        ["5. Realizacja", "Czas realizacji ustalany jest indywidualnie i potwierdzany po złożeniu zamówienia."],
        ["6. Kontakt", "W sprawach związanych z zamówieniem prosimy o kontakt na adres e-mail podany w sekcji Kontakt."],
      ],
      en: [
        ["1. General", "These terms define the rules for using the BOHATERO online store and concluding sales agreements for personalized, printed children's books."],
        ["2. Orders", "An order is placed by selecting a package, completing the personalization form and making a payment. After ordering, we will contact you to confirm details and securely collect photos."],
        ["3. Personalized product", "Each book is created individually based on the information and photos provided. Illustrations are inspired by the child's appearance and preserve their characteristic features."],
        ["4. Payments", "Payments are processed via a secure payment provider. Prices are shown in PLN and include the product value."],
        ["5. Fulfilment", "Fulfilment time is agreed individually and confirmed after the order is placed."],
        ["6. Contact", "For order-related matters, please contact the e-mail address in the Contact section."],
      ],
    },
  },
  prywatnosc: {
    title: { pl: "Polityka prywatności", en: "Privacy Policy" },
    body: {
      pl: [
        ["Ochrona danych dziecka", "Prywatność najmłodszych traktujemy z najwyższą starannością. Zdjęcia oraz informacje o dziecku wykorzystujemy wyłącznie w celu stworzenia zamówionej książki."],
        ["Zgoda rodzica / opiekuna", "Przesłanie zdjęć i danych dziecka wymaga zgody rodzica lub opiekuna prawnego. Składając zamówienie, potwierdzasz, że posiadasz odpowiednie uprawnienia do udostępnienia tych materiałów."],
        ["Zakres wykorzystania", "Dane i zdjęcia nie są wykorzystywane w celach marketingowych ani udostępniane podmiotom trzecim w celach innych niż realizacja zamówienia."],
        ["Twoje prawa", "W każdej chwili możesz zwrócić się o wgląd, poprawienie lub usunięcie przekazanych danych, kontaktując się z nami."],
        ["Uwaga", "Szczegółowe, techniczne środki bezpieczeństwa są sukcesywnie wdrażane. W razie pytań dotyczących przetwarzania danych prosimy o kontakt."],
      ],
      en: [
        ["Protecting children's data", "We treat children's privacy with the utmost care. Photos and information about the child are used solely to create the ordered book."],
        ["Parent / guardian consent", "Uploading a child's photos and data requires the consent of a parent or legal guardian. By placing an order you confirm you have the right to share these materials."],
        ["Scope of use", "Data and photos are not used for marketing or shared with third parties for purposes other than fulfilling your order."],
        ["Your rights", "You may request access, correction or deletion of the data you provided at any time by contacting us."],
        ["Note", "Detailed technical security measures are being progressively implemented. For any questions about data processing, please contact us."],
      ],
    },
  },
  dostawa: {
    title: { pl: "Dostawa", en: "Delivery" },
    body: {
      pl: [
        ["Wysyłka", "Gotowa książka jest starannie pakowana i wysyłana pod wskazany adres. Szczegóły dostawy potwierdzamy po realizacji zamówienia."],
        ["Pakiety cyfrowe", "Animowane filmy dostępne w wybranych pakietach dostarczane są cyfrowo, bezpośrednio do klienta."],
        ["Opakowanie prezentowe", "Na życzenie książkę możemy przygotować w wersji prezentowej."],
      ],
      en: [
        ["Shipping", "The finished book is carefully packaged and shipped to the address provided. Delivery details are confirmed after the order is fulfilled."],
        ["Digital packages", "Animated clips included in selected packages are delivered digitally, directly to the customer."],
        ["Gift wrapping", "On request, we can prepare the book in a gift-ready edition."],
      ],
    },
  },
  zwroty: {
    title: { pl: "Zwroty i reklamacje", en: "Returns & complaints" },
    body: {
      pl: [
        ["Produkt personalizowany", "Ze względu na indywidualny, personalizowany charakter produktu, książki tworzone są na specjalne zamówienie."],
        ["Reklamacje", "Jeśli produkt dotarł uszkodzony lub zawiera wadę powstałą po naszej stronie, skontaktuj się z nami — rozpatrzymy zgłoszenie i zaproponujemy rozwiązanie (poprawka, ponowny druk lub inne)."],
        ["Kontakt", "Zgłoszenia reklamacyjne prosimy kierować na adres e-mail podany w sekcji Kontakt, dołączając zdjęcia i opis problemu."],
      ],
      en: [
        ["Personalized product", "Due to the individual, personalized nature of the product, books are made to special order."],
        ["Complaints", "If the product arrived damaged or contains a defect on our side, contact us — we will review the case and propose a solution (correction, reprint or other)."],
        ["Contact", "Please send complaints to the e-mail in the Contact section, including photos and a description of the issue."],
      ],
    },
  },
  about: {
    title: { pl: "O nas", en: "About us" },
    body: {
      pl: [["Nasza misja", "BOHATERO tworzy wyjątkowe, drukowane książki, w których dziecko staje się prawdziwym bohaterem własnej przygody. Wierzymy, że każde dziecko zasługuje na historię stworzoną specjalnie dla niego."]],
      en: [["Our mission", "BOHATERO creates unique, printed books in which a child becomes the real hero of their own adventure. We believe every child deserves a story made just for them."]],
    },
  },
  contact: {
    title: { pl: "Kontakt", en: "Contact" },
    body: {
      pl: [["Napisz do nas", "W sprawach zamówień i pytań ogólnych prosimy o kontakt e-mailowy. Odpowiadamy najszybciej, jak to możliwe."], ["E-mail", "kontakt@bohatero.pl"]],
      en: [["Get in touch", "For orders and general questions, please contact us by e-mail. We reply as soon as possible."], ["E-mail", "kontakt@bohatero.pl"]],
    },
  },
};

export default function LegalPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const c = CONTENT[slug];
  const t = (o) => (o ? o[lang] || o.pl : "");

  return (
    <div className="min-h-screen">
      <header className="backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#E7E0D4] sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo"><Sparkles className="text-[#D4AF37]" size={20} /><span className="font-display text-2xl">BOHATERO</span></Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#1A243A]"><ArrowLeft size={16} /> {lang === "pl" ? "Strona główna" : "Home"}</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        {!c ? <p>404</p> : <>
          <h1 className="font-display text-4xl sm:text-5xl">{t(c.title)}</h1>
          <div className="mt-10 space-y-8">
            {t(c.body).map((s, i) => (
              <div key={i}>
                <h2 className="font-display text-2xl">{s[0]}</h2>
                <p className="mt-2 text-[#1A243A]/80 leading-relaxed">{s[1]}</p>
              </div>
            ))}
          </div>
        </>}
      </main>
    </div>
  );
}
